import { useCallback, useMemo, useReducer } from 'react';

enum EditCommand {
  SET,
  UPDATE,
  CLEAR,
}

type EditState<T> = {
  isChanged: boolean;
  data?: T | null;
};

type EditAction<T> =
  | { command: EditCommand.SET; data: T; isChanged: boolean }
  | { command: EditCommand.UPDATE; data: Partial<T> }
  | { command: EditCommand.CLEAR };

function useEditState<T>(
  initialData?: T | null,
  getIsMissingFields?: (data?: T | null) => boolean,
) {
  const reducer = (
    state: EditState<T>,
    action: EditAction<T>,
  ): EditState<T> => {
    switch (action.command) {
      case EditCommand.SET:
        return { data: action.data, isChanged: action.isChanged };
      case EditCommand.UPDATE:
        return {
          data: { ...state.data, ...action.data } as T,
          isChanged: true,
        };
      case EditCommand.CLEAR:
        return { data: null, isChanged: true };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    data: initialData ?? null,
    isChanged: false,
  });

  const reset = useCallback(
    (data: T, isChanged = false) =>
      dispatch({ command: EditCommand.SET, data, isChanged }),
    [],
  );

  const update = useCallback(
    (data: Partial<T>) => dispatch({ command: EditCommand.UPDATE, data }),
    [],
  );

  const clear = useCallback(() => dispatch({ command: EditCommand.CLEAR }), []);

  const isMissingFields = useMemo(
    () => (getIsMissingFields ? getIsMissingFields(state.data) : false),
    [state.data, getIsMissingFields],
  );

  return {
    clear,
    data: state.data,
    isChanged: state.isChanged,
    isMissingFields,
    reset,
    update,
  };
}

export default useEditState;
